import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { Button, Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { CycleTemplateStep } from './steps/CycleTemplateStep'
import { DataMappingStep } from './steps/DataMappingStep'
import { RoleMappingStep } from './steps/RoleMappingStep'
import type { FieldMapping, RoleMapping } from './types'
import { WizardNavigation } from './WizardNavigation'
import { WizardStepIndicator } from './WizardStepIndicator'
import { normalizeFieldMappings, parseRoleMappings } from './wizardUtils'

const WIZARD_STEPS = [
	{ id: 'setup', label: 'Webhook Setup' },
	{ id: 'mapping', label: 'Data Mapping' },
	{ id: 'template', label: 'Cycle Template' },
	{ id: 'roles', label: 'Role Mapping' },
]

const STEP = {
	setup: 0,
	mapping: 1,
	template: 2,
	roles: 3,
} as const satisfies Record<(typeof WIZARD_STEPS)[number]['id'], number>

interface WebhookWizardProps {
	isOpen: boolean
	onClose: () => void
	onComplete: () => void
	editingIntegrationId?: string
}

type RouterOutput = inferRouterOutputs<AppRouter>
type CrmIntegrationDetails = RouterOutput['admin']['integrations']['crm']['getById']

function getSaveLabel(isSaving: boolean, isEditMode: boolean): string {
	if (isSaving) return 'Saving...'
	return isEditMode ? 'Save Changes' : 'Create Integration'
}

function getNextLabel(isPending: boolean, isCreatingOnStep0: boolean, hasCreated: boolean): string {
	if (isPending) return 'Creating...'
	if (isCreatingOnStep0 && !hasCreated) return 'Create & Continue'
	return 'Next →'
}

function checkStepValid(
	step: number,
	integrationName: string,
	fieldMappings: FieldMapping[],
	selectedTemplateId: string,
): boolean {
	switch (step) {
		case STEP.setup:
			return !!integrationName.trim()
		case STEP.mapping:
			return fieldMappings.filter((m) => m.crmField && m.viaPeopleField).length > 0
		case STEP.template:
			return !!selectedTemplateId
		case STEP.roles:
			return true
		default:
			return false
	}
}

function WebhookCredentials({
	url,
	secret,
	secretRevealed,
	onToggleReveal,
	onCopy,
}: Readonly<{
	url: string
	secret?: string
	secretRevealed: boolean
	onToggleReveal: () => void
	onCopy: (text: string, label: string) => void
}>) {
	return (
		<>
			<div>
				<span className='block text-xs font-medium text-neutral-600 mb-1'>Webhook URL</span>
				<div className='flex gap-2'>
					<input
						type='text'
						readOnly
						value={url}
						className='flex-1 px-3 py-1.5 bg-white border border-neutral-200 rounded text-xs font-mono text-neutral-800'
					/>
					<Button variant='secondary' size='small' onClick={() => onCopy(url, 'URL')}>
						Copy
					</Button>
				</div>
			</div>
			{secret && (
				<div>
					<span className='block text-xs font-medium text-neutral-600 mb-1'>
						Webhook Secret (for HMAC-SHA256 signature)
					</span>
					<div className='flex gap-2'>
						<input
							type={secretRevealed ? 'text' : 'password'}
							readOnly
							value={secret}
							className='flex-1 px-3 py-1.5 bg-white border border-neutral-200 rounded text-xs font-mono text-neutral-800'
						/>
						<Button variant='secondary' size='small' onClick={onToggleReveal}>
							{secretRevealed ? 'Hide' : 'Reveal'}
						</Button>
						<Button variant='secondary' size='small' onClick={() => onCopy(secret, 'Secret')}>
							Copy
						</Button>
					</div>
				</div>
			)}
		</>
	)
}

export function WebhookWizard({ isOpen, onClose, onComplete, editingIntegrationId }: Readonly<WebhookWizardProps>) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const isEditMode = !!editingIntegrationId

	const [currentStep, setCurrentStep] = useState(0)
	const [secretRevealed, setSecretRevealed] = useState(false)

	// Step 1: Webhook Setup
	const [integrationName, setIntegrationName] = useState('')

	// Step 2: Data Mapping
	const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])

	// Step 3: Cycle Template
	const [selectedTemplateId, setSelectedTemplateId] = useState('')

	// Step 4: Role Mapping
	const [roleMappings, setRoleMappings] = useState<RoleMapping[]>([])

	// Created integration data (after step 1 save, create mode only)
	const [createdIntegration, setCreatedIntegration] = useState<{
		id: string
		webhookSecret: string
	} | null>(null)

	// Track if edit data has been loaded
	const [editDataLoaded, setEditDataLoaded] = useState(false)

	// Load existing integration data in edit mode
	const {
		data: existingData,
		isLoading: isLoadingEditData,
		isError: isEditDataError,
		refetch: refetchEditData,
	} = useQuery({
		...trpc.admin.integrations.crm.getById.queryOptions({ id: editingIntegrationId as string }),
		enabled: isEditMode && !!editingIntegrationId,
	})

	// Pre-populate form when edit data loads
	useEffect(() => {
		if (isEditMode && existingData && !editDataLoaded) {
			const { integration, fieldMappings: fMappings } = existingData as CrmIntegrationDetails

			if (integration) {
				setIntegrationName(integration.name)
				setSelectedTemplateId(integration.templateId || integration.targetCycleId || '')
			}

			if (fMappings && fMappings.length > 0) {
				setFieldMappings(normalizeFieldMappings(fMappings))
			}

			if (Array.isArray(integration?.crmRoleMappings)) {
				const valid = parseRoleMappings(integration.crmRoleMappings as unknown[])
				if (valid.length > 0) setRoleMappings(valid)
			}

			setEditDataLoaded(true)
		}
	}, [isEditMode, existingData, editDataLoaded])

	// Mapping mutations
	const fieldMappingsMutation = useMutation(trpc.admin.integrations.crm.upsertFieldMappings.mutationOptions())
	const roleMappingsMutation = useMutation(trpc.admin.integrations.crm.upsertRoleMappings.mutationOptions())

	const saveMappings = async (integrationId: string) => {
		// Both crmField and viaPeopleField must be filled: the API schema requires non-empty
		// source/target, so half-filled custom rows would crash the whole save with a Zod error
		// instead of being silently dropped here.
		const validFieldMappings = fieldMappings
			.filter((m) => m.crmField && m.viaPeopleField)
			.map((m) => ({
				sourceField: m.crmField,
				targetField: m.viaPeopleField,
				transform: m.transform || undefined,
			}))

		if (validFieldMappings.length > 0) {
			await fieldMappingsMutation.mutateAsync({
				integrationId,
				mappings: validFieldMappings,
			})
		}

		const validRoleMappings = roleMappings.filter((m) => m.crmRoleValue && m.roleId && m.formId)
		await roleMappingsMutation.mutateAsync({ integrationId, mappings: validRoleMappings })
	}

	// Update mutation (edit mode)
	const updateMutation = useMutation({
		...trpc.admin.integrations.crm.update.mutationOptions(),
		onSuccess: async () => {
			try {
				await saveMappings(editingIntegrationId as string)
				showToast('Webhook integration updated successfully')
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
						organizationId: organizationId as string,
					}),
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.integrations.crm.getById.queryKey({ id: editingIntegrationId as string }),
				})
				resetWizard()
				onComplete()
			} catch {
				showToast('Integration updated but failed to save mappings. Please try again.', 'error')
			}
		},
		onError: (error) => {
			showToast(error.message || 'Failed to update integration. Please try again.', 'error')
		},
	})

	// Create mutation (create mode)
	const createMutation = useMutation({
		...trpc.admin.integrations.crm.create.mutationOptions(),
		onSuccess: (data) => {
			const integration = data as { id: string; webhookSecret: string }
			setCreatedIntegration(integration)
			showToast('Webhook integration created')
		},
		onError: () => {
			showToast('Failed to create integration. Please try again.', 'error')
		},
	})

	// Final save mutation (create mode)
	const saveMappingsMutation = useMutation({
		mutationFn: async () => {
			if (!createdIntegration) throw new Error('Integration not created')
			await saveMappings(createdIntegration.id)
		},
		onSuccess: () => {
			showToast('Webhook integration configured successfully')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
					organizationId: organizationId as string,
				}),
			})
			resetWizard()
			onComplete()
		},
		onError: () => {
			showToast('Integration created but failed to save mappings. You can edit them later.', 'error')
			resetWizard()
			onComplete()
		},
	})

	// Delete mutation for cleanup on cancel (create mode only)
	const deleteMutation = useMutation({
		...trpc.admin.integrations.crm.delete.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
					organizationId: organizationId as string,
				}),
			})
			resetWizard()
			onClose()
		},
		onError: () => {
			resetWizard()
			onClose()
		},
	})

	const resetWizard = () => {
		setCurrentStep(0)
		setIntegrationName('')
		setFieldMappings([])
		setSelectedTemplateId('')
		setRoleMappings([])
		setCreatedIntegration(null)
		setEditDataLoaded(false)
		setSecretRevealed(false)
	}

	const isCreatingOnStep0 = !isEditMode && currentStep === STEP.setup
	const shouldCleanupOnClose = !isEditMode && !!createdIntegration && currentStep > 0
	const isCreatingNewWebhook = isCreatingOnStep0 && !createdIntegration

	const handleClose = () => {
		if (shouldCleanupOnClose && createdIntegration) {
			deleteMutation.mutate({ id: createdIntegration.id })
		} else {
			resetWizard()
			onClose()
		}
	}

	const handleNext = async () => {
		if (isCreatingNewWebhook) {
			createMutation.mutate({
				organizationId: organizationId as string,
				name: integrationName,
				provider: 'webhook',
				targetCycleId: selectedTemplateId || undefined,
			})
			return
		}
		if (currentStep < WIZARD_STEPS.length - 1) {
			setCurrentStep(currentStep + 1)
		}
	}

	const handleNextAfterCreate = () => {
		setCurrentStep(STEP.mapping)
	}

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1)
		}
	}

	const handleSave = () => {
		if (!isEditMode) {
			saveMappingsMutation.mutate()
			return
		}
		if (!editingIntegrationId) return
		updateMutation.mutate({
			id: editingIntegrationId,
			name: integrationName,
			templateId: selectedTemplateId || undefined,
		})
	}

	const isStepValid = (step: number) => checkStepValid(step, integrationName, fieldMappings, selectedTemplateId)

	const isLastStep = currentStep === WIZARD_STEPS.length - 1
	const isSaving =
		updateMutation.isPending ||
		createMutation.isPending ||
		saveMappingsMutation.isPending ||
		fieldMappingsMutation.isPending ||
		roleMappingsMutation.isPending
	const saveLabel = getSaveLabel(isSaving, isEditMode)
	const nextLabel = getNextLabel(createMutation.isPending, isCreatingOnStep0, !!createdIntegration)
	const nextOnClick = isCreatingOnStep0 && createdIntegration ? handleNextAfterCreate : handleNext
	const isNextDisabled = !isStepValid(currentStep) || createMutation.isPending
	const webhookUrl = createdIntegration
		? `${globalThis.location.origin}/api/webhooks/crm?provider=webhook&integrationId=${createdIntegration.id}`
		: null

	const editIntegration = isEditMode && existingData ? (existingData as CrmIntegrationDetails).integration : null
	const editWebhookUrl = editIntegration
		? `${globalThis.location.origin}/api/webhooks/crm?provider=webhook&integrationId=${editIntegration.id}`
		: null
	const editWebhookSecret = editIntegration?.webhookSecret ?? null

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={isEditMode ? 'Edit Webhook Integration' : 'New Webhook Integration'}
			className='!w-[700px] md:!w-[800px]'
			closeOnOverlayClick={false}>
			<div className='flex flex-col min-h-[400px]'>
				<WizardStepIndicator steps={WIZARD_STEPS} activeStep={currentStep} />

				<div className='h-px bg-neutral-100 w-full' />

				{/* Step Content */}
				<div className='flex-1 py-6 overflow-y-auto'>
					{isLoadingEditData || (isEditMode && !editDataLoaded && !isEditDataError) ? (
						<SpinnerWithBackground backgroundClass='opacity-100' />
					) : isEditMode && isEditDataError ? (
						<div className='flex flex-col items-center justify-center py-12 gap-3 text-center'>
							<Text variant='medium' weight='semibold' className='text-red-600'>
								Failed to load integration
							</Text>
							<Text variant='small' className='text-neutral-600'>
								Unable to load the integration details. Please try again.
							</Text>
							<Button variant='secondary' size='small' onClick={() => refetchEditData()}>
								Retry
							</Button>
						</div>
					) : (
						<>
							{currentStep === STEP.setup && (
								<div className='flex flex-col gap-5'>
									<div>
										<Text variant='medium' weight='semibold' className='mb-1'>
											Webhook Setup
										</Text>
										<Text variant='small' className='text-neutral-600'>
											{isEditMode
												? 'Update the name for this webhook integration.'
												: 'Create a webhook endpoint that external systems can call to trigger cycles.'}
										</Text>
									</div>

									{/* Name */}
									<div>
										<span className='block text-sm font-medium text-neutral-700 mb-1'>Integration Name</span>
										<input
											type='text'
											value={integrationName}
											onChange={(e) => setIntegrationName(e.target.value)}
											placeholder='e.g., Deal Closed Webhook'
											className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
											disabled={!isEditMode && !!createdIntegration}
										/>
									</div>

									{/* Show credentials in edit mode */}
									{isEditMode && editWebhookUrl && (
										<div className='border border-neutral-200 bg-neutral-50 rounded-lg p-4 flex flex-col gap-3'>
											<Text variant='small' weight='semibold' className='text-neutral-700'>
												Webhook Credentials
											</Text>
											<WebhookCredentials
												url={editWebhookUrl}
												secret={editWebhookSecret || undefined}
												secretRevealed={secretRevealed}
												onToggleReveal={() => setSecretRevealed((r) => !r)}
												onCopy={(text, label) => {
													navigator.clipboard.writeText(text)
													showToast(`Copied ${label}`)
												}}
											/>
										</div>
									)}

									{/* Show credentials after creation (create mode only) */}
									{!isEditMode && createdIntegration && (
										<div className='border border-primary-200 bg-primary-50 rounded-lg p-4 flex flex-col gap-3'>
											<Text variant='small' weight='semibold' className='text-primary-800'>
												✓ Webhook Created — Copy these credentials
											</Text>
											<WebhookCredentials
												url={webhookUrl || ''}
												secret={createdIntegration.webhookSecret}
												secretRevealed={secretRevealed}
												onToggleReveal={() => setSecretRevealed((r) => !r)}
												onCopy={(text, label) => {
													navigator.clipboard.writeText(text)
													showToast(`Copied ${label}`)
												}}
											/>

											<div className='bg-white border border-neutral-200 rounded p-3 mt-1'>
												<Text variant='xsmall' className='text-neutral-600 font-medium mb-1'>
													Signature Header
												</Text>
												<Text variant='xsmall' className='text-neutral-500'>
													Include{' '}
													<code className='bg-neutral-100 px-1 py-0.5 rounded text-xs'>x-webhook-signature</code> header
													with HMAC-SHA256 hex digest of the raw request body using the secret above.
												</Text>
											</div>
										</div>
									)}
								</div>
							)}
							{currentStep === STEP.mapping && (
								<DataMappingStep provider='webhook' fieldMappings={fieldMappings} onChange={setFieldMappings} />
							)}
							{currentStep === STEP.template && (
								<CycleTemplateStep selectedTemplateId={selectedTemplateId} onChange={setSelectedTemplateId} />
							)}
							{currentStep === STEP.roles && <RoleMappingStep roleMappings={roleMappings} onChange={setRoleMappings} />}
						</>
					)}
				</div>

				<WizardNavigation
					currentStep={currentStep}
					onBack={handleBack}
					onClose={handleClose}
					actionButton={
						isLastStep ? (
							<Button variant='primary' onClick={handleSave} disabled={!isStepValid(currentStep) || isSaving}>
								{saveLabel}
							</Button>
						) : (
							<Button variant='primary' onClick={nextOnClick} disabled={isNextDisabled}>
								{nextLabel}
							</Button>
						)
					}
				/>

				{/* Validation hint */}
				{!isStepValid(currentStep) && (
					<div className='mt-2'>
						<Text variant='xsmall' className='text-neutral-400 text-right'>
							{currentStep === STEP.setup && 'Enter an integration name'}
							{currentStep === STEP.mapping && 'Map at least one field'}
							{currentStep === STEP.template && 'Select a cycle template'}
						</Text>
					</div>
				)}
			</div>

			{isSaving && <SpinnerWithBackground />}
		</Modal>
	)
}
